/**
 * Listings Data Wrangler — API shape helpers and serializer-aligned field sets.
 * Derived from legacy Django `DirectoryEntrySerializer` (hoodie/serializers.py).
 */

/** Fields present on DirectoryEntrySerializer (writable via ModelSerializer unless read_only). */
export const SERIALIZER_LISTING_FIELDS = [
  "id",
  "business_name",
  "tags",
  "contact_name",
  "contact_title",
  "website",
  "email",
  "phone",
  "facebook_url",
  "twitter_handle",
  "instagram_name",
  "linkedin_url",
  "short_description",
  "extended_description",
  "related_groups",
  "graphic",
  "graphic_400",
  "graphic_800",
  "heart_count",
  "slug",
];

/**
 * Scalar text/url/email/phone fields on DirectoryEntrySerializer (production PATCH allowlist).
 * Excludes: tags, graphic, slug, id, computed fields, M2M.
 */
export const PATCH_TEXT_FIELDS = [
  "business_name",
  "contact_name",
  "contact_title",
  "website",
  "email",
  "phone",
  "facebook_url",
  "twitter_handle",
  "instagram_name",
  "linkedin_url",
  "short_description",
  "extended_description",
];

/** Model/admin fields not exposed on DirectoryEntrySerializer. */
export const API_ABSENT_FIELDS = [
  "recommended_by",
  "enabled",
  "recommendation_verified",
  "recommendation_notes",
  "administrator",
  "subscription_active",
  "active",
];

export const BLOCKED_ADMIN_FIELDS = [
  "enabled",
  "recommendation_verified",
  "recommendation_notes",
  "administrator",
  "related_groups",
  "subscription_active",
  "active",
  "graphic",
  "recommended_by",
];

export function isDevMode() {
  return process.env.NODE_ENV === "development";
}

/**
 * @param {unknown} data
 * @returns {{ results: any[], count: number|null, next: string|null, previous: string|null, isPaginated: boolean }}
 */
export function normalizeListingsResponse(data) {
  if (data == null) {
    return {
      results: [],
      count: null,
      next: null,
      previous: null,
      isPaginated: false,
    };
  }
  if (Array.isArray(data)) {
    return {
      results: data,
      count: data.length,
      next: null,
      previous: null,
      isPaginated: false,
    };
  }
  if (typeof data === "object" && Array.isArray(data.results)) {
    return {
      results: data.results,
      count: typeof data.count === "number" ? data.count : null,
      next: data.next ?? null,
      previous: data.previous ?? null,
      isPaginated: true,
    };
  }
  return {
    results: [],
    count: null,
    next: null,
    previous: null,
    isPaginated: false,
  };
}

/**
 * Tags list endpoint may return a bare array or paginated envelope.
 * @param {unknown} data
 * @returns {any[]}
 */
export function normalizeTagsList(data) {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "object" && Array.isArray(data.results)) return data.results;
  return [];
}

/**
 * @param {unknown} tags
 * @returns {number[]}
 */
export function extractTagIds(tags) {
  if (!Array.isArray(tags)) return [];
  if (tags.length === 0) return [];
  const first = tags[0];
  if (typeof first === "number") return tags.map((t) => Number(t));
  if (typeof first === "object" && first != null && "id" in first) {
    return tags.map((t) => Number(t.id)).filter((id) => Number.isFinite(id));
  }
  return [];
}

/**
 * @param {Record<string, unknown>|null|undefined} listing
 * @param {number[]|undefined} tagsSelection
 */
export function pickEditableForm(listing, tagsSelection) {
  const out = {};
  PATCH_TEXT_FIELDS.forEach((k) => {
    const v = listing?.[k];
    out[k] = v === null || v === undefined ? "" : String(v);
  });
  if (tagsSelection !== undefined) {
    out.tags = tagsSelection;
  }
  return out;
}

/** True when any production-editable text field differs. */
export function textFieldsDirty(initial, current) {
  if (!initial || !current) return false;
  return PATCH_TEXT_FIELDS.some(
    (k) => String(initial[k] ?? "") !== String(current[k] ?? "")
  );
}

export function formsEqual(a, b) {
  if (!a || !b) return a === b;
  return !textFieldsDirty(a, b);
}

/**
 * Dirty text fields only (for PATCH without tags).
 * @param {Record<string, unknown>} initial
 * @param {Record<string, unknown>} current
 */
export function buildTextPatch(initial, current) {
  const patch = {};
  PATCH_TEXT_FIELDS.forEach((k) => {
    const a = String(initial?.[k] ?? "");
    const b = String(current?.[k] ?? "");
    if (a !== b) patch[k] = b;
  });
  return patch;
}

/** Production save payload: changed serializer text fields only (never tags). */
export function buildProductionTextPatch(initial, current) {
  return buildTextPatch(initial, current);
}

/**
 * @param {Error & { data?: unknown, status?: number }} err
 */
export function formatApiError(err) {
  if (!err) return "Unknown error";
  const data = err.data;
  if (data && typeof data === "object") {
    if (typeof data.detail === "string") return data.detail;
    const parts = [];
    for (const [key, val] of Object.entries(data)) {
      if (key === "detail") continue;
      if (Array.isArray(val)) parts.push(`${key}: ${val.join(", ")}`);
      else if (val != null) parts.push(`${key}: ${String(val)}`);
    }
    if (parts.length) return parts.join("; ");
  }
  return err.message || String(err);
}

/**
 * @param {unknown} listing
 */
export function listingResponseFieldKeys(listing) {
  if (!listing || typeof listing !== "object") return [];
  return Object.keys(listing).sort();
}
